import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { buildUrl } from '@shared/utils';
import { mpeApi } from '@retail/mgpris/config';
import { useMPEFetch } from '@retail/app/hooks';
import {
  CompetitorChangedPriceDTO,
  ConditionPartitionedCountDTO,
  IntervalType,
  NewAssortmentItemDTO,
} from '@retail/dashboard/types';
import { QueryOptions } from '@shared/fetch-utils';

interface DashboardProps extends QueryOptions {
  assortmentId: number;
  from: string;
  to: string;
  interval?: IntervalType;
}

export function useFetchNewAssortmentItems({
  assortmentId,
  from,
  to,
  ...queryOptions
}: DashboardProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<NewAssortmentItemDTO[]>({
    method: 'GET',
    queryKey: ['dashboard-new-assortment-items', assortmentId, from, to],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.dashboard.newAssortmentItems.getFullLinkPath(),
      searchParams: {
        assortmentId,
        from,
        to,
      },
    }),
    ...queryOptions,
  });
}

export function useFetchUpdatedAgreementPrices({
  assortmentId,
  from,
  to,
  ...queryOptions
}: DashboardProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<ConditionPartitionedCountDTO[]>({
    method: 'GET',
    queryKey: ['updated-agreement-prices', assortmentId, from, to],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.dashboard.updatedAgreementPrices.getFullLinkPath(),
      searchParams: {
        assortmentId,
        from,
        to,
      },
    }),
    ...queryOptions,
  });
}

export function useFetchUpdatedCompetitorPrices({
  assortmentId,
  from,
  to,
  onlyLatest,
  interval,
  ...queryOptions
}: DashboardProps & { onlyLatest?: boolean }) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<CompetitorChangedPriceDTO[]>({
    method: 'GET',
    queryKey: ['updated-competitor-prices', assortmentId, from, to, onlyLatest],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.dashboard.competitorPricesCount.getFullLinkPath(),
      searchParams: {
        assortmentId,
        from,
        to,
        onlyLatest,
        interval: interval === 'HALF_YEAR' ? 'MONTH' : interval === 'MONTH' ? 'WEEK' : 'DAY',
      },
    }),
    ...queryOptions,
  });
}
